'use client';

import { useIntegrationSetupFlow } from './useIntegrationSetupFlow';
import { IntegrationSetupError } from './IntegrationSetupError';
import { useConnectorDraft } from './useConnectorDraft';

import {
  Badge,
  Box,
  Button,
  Field,
  Grid,
  GridItem,
  HStack,
  Input,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  AlertCircle,
  BarChart3,
  Check,
  Clipboard,
  Cloud,
  Database,
  KeyRound,
  MousePointerClick,
  Plug,
  RefreshCw,
  Search,
  ShieldCheck,
  Unlink,
  Wrench,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/app/auth-provider';
import { GSCIntegration } from '@/components/shared/integrations/GSCIntegration';
import {
  IntegrationCard,
  IntegrationNotice,
  IntegrationStatusRow,
  useIntegrationSurfaceMode,
} from '@/components/shared/integrations/IntegrationCardShell';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { useProject } from '@/contexts/ProjectContext';

type Provider = 'ga4' | 'clarity' | 'bing_webmaster' | 'cloudflare';
type IntegrationProvider = Provider | 'gsc';
export type AnalyticsIntegrationProvider = IntegrationProvider;
const GSC_PROVIDER_LABEL = 'Google Search Console';

interface AnalyticsSource {
  id: string;
  provider: IntegrationProvider;
  displayName: string;
  externalId: string;
  propertyUrl: string | null;
  exposeToMcp: boolean;
  status: string;
  statusDetail: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  sourceConfig?: {
    scope?: string | null;
    logpushEnabled?: boolean;
    logpushJobId?: number | null;
  };
}

interface AnalyticsStatus {
  projectId: string;
  workspaceId: string;
  ga4ServiceAccountEmail: string;
  ga4ServiceAccount?: {
    email: string;
    status: 'exists' | 'created' | 'not_configured' | 'error';
    tokenCreatorBinding: 'ensured' | 'not_configured' | 'error';
    error?: string;
  };
  sources: AnalyticsSource[];
}

const PROVIDER_COPY: Record<
  Provider,
  {
    title: string;
    description: string;
    icon: React.ReactNode;
    primaryPlaceholder: string;
    secretPlaceholder?: string;
  }
> = {
  ga4: {
    title: 'Google Analytics',
    description: 'GA4 page, channel, conversion, and revenue aggregates.',
    icon: <BarChart3 size={18} />,
    primaryPlaceholder: 'GA4 property ID, for example 123456789',
  },
  clarity: {
    title: 'Microsoft Clarity',
    description: 'UX friction snapshots from the Clarity Data Export API.',
    icon: <MousePointerClick size={18} />,
    primaryPlaceholder: 'Project URL or label',
    secretPlaceholder: 'Data Export API token',
  },
  bing_webmaster: {
    title: 'Bing Webmaster Tools',
    description: 'Bing page and query search performance aggregates.',
    icon: <Search size={18} />,
    primaryPlaceholder: 'Verified site URL, optional',
    secretPlaceholder: 'Bing Webmaster API key',
  },
  cloudflare: {
    title: 'Cloudflare',
    description: 'Web Analytics traffic, Core Web Vitals, and AI crawler activity.',
    icon: <Cloud size={18} />,
    primaryPlaceholder: 'Cloudflare account ID',
    secretPlaceholder: 'Cloudflare API token',
  },
};

const PROVIDER_CATALOG: Record<
  IntegrationProvider,
  {
    title: string;
    shortTitle: string;
    description: string;
    icon: React.ReactNode;
    tools: string[];
    auth: string;
  }
> = {
  ga4: {
    title: PROVIDER_COPY.ga4.title,
    shortTitle: 'GA4',
    description: PROVIDER_COPY.ga4.description,
    icon: PROVIDER_COPY.ga4.icon,
    tools: [
      'analytics.sources',
      'analytics.pageMetrics',
      'analytics.channelMetrics',
      'website.performance',
    ],
    auth: 'Managed reader',
  },
  gsc: {
    title: GSC_PROVIDER_LABEL,
    shortTitle: 'GSC',
    description: 'Google organic query, page, click, impression, CTR, and position data.',
    icon: <Search size={18} />,
    tools: ['analytics.queryMetrics', 'analytics.pageMetrics', 'website.performance'],
    auth: 'OAuth',
  },
  bing_webmaster: {
    title: PROVIDER_COPY.bing_webmaster.title,
    shortTitle: 'Bing',
    description: PROVIDER_COPY.bing_webmaster.description,
    icon: PROVIDER_COPY.bing_webmaster.icon,
    tools: ['analytics.queryMetrics', 'analytics.pageMetrics', 'website.performance'],
    auth: 'API key',
  },
  clarity: {
    title: PROVIDER_COPY.clarity.title,
    shortTitle: 'Clarity',
    description: PROVIDER_COPY.clarity.description,
    icon: PROVIDER_COPY.clarity.icon,
    tools: ['analytics.uxSignals', 'website.performance'],
    auth: 'Export token',
  },
  cloudflare: {
    title: PROVIDER_COPY.cloudflare.title,
    shortTitle: 'Cloudflare',
    description: PROVIDER_COPY.cloudflare.description,
    icon: PROVIDER_COPY.cloudflare.icon,
    tools: [
      'analytics.pageMetrics',
      'analytics.channelMetrics',
      'analytics.aiCrawlers',
      'website.performance',
    ],
    auth: 'API token',
  },
};

const PROVIDER_ORDER: IntegrationProvider[] = [
  'ga4',
  'gsc',
  'bing_webmaster',
  'clarity',
  'cloudflare',
];

function sourceTone(source?: AnalyticsSource): 'connected' | 'warning' | 'idle' | 'danger' {
  if (!source) return 'idle';
  if (source.lastSyncStatus === 'failed') return 'danger';
  if (source.status === 'connected') return 'connected';
  if (source.status === 'error') return 'danger';
  return 'warning';
}

function sourcesTone(sources: AnalyticsSource[]): 'connected' | 'warning' | 'idle' | 'danger' {
  if (sources.length === 0) return 'idle';
  const tones = sources.map(sourceTone);
  if (tones.includes('danger')) return 'danger';
  if (tones.includes('warning')) return 'warning';
  return 'connected';
}

function sourcesStatusLabel(sources: AnalyticsSource[]): string {
  if (sources.length === 0) return 'Needs setup';
  return sources.length === 1 ? 'Connected' : `${sources.length} connected`;
}

function formatDate(value: string | null): string {
  if (!value) return 'Never';
  return new Date(value).toLocaleString();
}

function isCloudflareAccountTokenValue(value: string): boolean {
  return /^cfat_/i.test(value.trim());
}

function ga4IdentityDescription(status: AnalyticsStatus | null): string {
  const serviceAccount = status?.ga4ServiceAccount;
  if (!serviceAccount)
    return 'Attune creates this automatically before you verify and connect GA4.';
  if (serviceAccount.status === 'created') {
    return 'Created automatically. Add this reader as Viewer in GA4 Property access management.';
  }
  if (serviceAccount.status === 'exists') {
    return 'Ready. Add this reader as Viewer in GA4 Property access management.';
  }
  if (serviceAccount.status === 'error') return 'Attune setup issue. This reader is not ready yet.';
  return 'Attune creates this automatically before you verify and connect GA4.';
}

function badgePalette(tone: 'connected' | 'warning' | 'idle' | 'danger'): string {
  if (tone === 'connected') return 'green';
  if (tone === 'warning') return 'orange';
  if (tone === 'danger') return 'red';
  return 'gray';
}

function ProviderToolButton({
  provider,
  selected,
  sources,
  onSelect,
}: {
  provider: IntegrationProvider;
  selected: boolean;
  sources: AnalyticsSource[];
  onSelect: () => void;
}) {
  const catalog = PROVIDER_CATALOG[provider];
  const tone = sourcesTone(sources);

  return (
    <Button
      variant="ghost"
      h="auto"
      w="full"
      p="3"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={selected ? 'accent.solid' : 'transparent'}
      borderRadius="glass"
      bg={selected ? 'ui.surface.selected' : 'transparent'}
      _hover={{ bg: 'ui.surface.selected', borderColor: 'ui.border.default' }}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <HStack w="full" justify="space-between" align="flex-start" gap="3">
        <HStack minW={0} align="flex-start" gap="3">
          <Box
            boxSize="8"
            borderRadius="glassButton"
            bg={selected ? 'ui.surface.card' : 'ui.surface.selected'}
            color="ui.text.primary"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            {catalog.icon}
          </Box>
          <Box minW={0} textAlign="left">
            <HStack gap="2" flexWrap="wrap">
              <Text fontSize="sm" fontWeight="650" color="ui.text.primary">
                {catalog.shortTitle}
              </Text>
              <Badge colorPalette="gray" variant="subtle" size="sm">
                {catalog.auth}
              </Badge>
            </HStack>
            <Text fontSize="xs" color="ui.text.tertiary" mt="0.5" lineClamp={1}>
              {catalog.title}
            </Text>
          </Box>
        </HStack>
        <Badge colorPalette={badgePalette(tone)} variant="subtle" size="sm" flexShrink={0}>
          {sources.length || 'New'}
        </Badge>
      </HStack>
    </Button>
  );
}

function ToolRailMetric({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <HStack justify="space-between" gap="3" color="ui.text.secondary">
      <HStack gap="2" minW={0}>
        {icon}
        <Text fontSize="xs">{label}</Text>
      </HStack>
      <Text fontSize="xs" fontWeight="600" color="ui.text.primary" textAlign="right">
        {value}
      </Text>
    </HStack>
  );
}

function ToolChip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <HStack
      gap="1.5"
      px="2"
      py="1"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="ui.border.subtle"
      borderRadius="glassButton"
      bg="ui.surface.selected"
      color="ui.text.secondary"
    >
      {icon}
      <Text fontSize="xs" fontWeight="600">
        {label}
      </Text>
    </HStack>
  );
}

interface EnterpriseAnalyticsIntegrationsProps {
  selectedProvider?: IntegrationProvider;
  showCatalog?: boolean;
  showProviderHeader?: boolean;
}

export function EnterpriseAnalyticsIntegrations({
  selectedProvider: selectedProviderOverride,
  showCatalog = true,
  showProviderHeader = showCatalog,
}: EnterpriseAnalyticsIntegrationsProps = {}) {
  const { user } = useAuth();
  const { currentProject } = useProject();
  const setup = useIntegrationSetupFlow(currentProject?.id);
  const surfaceMode = useIntegrationSurfaceMode();
  const isFlatSurface = surfaceMode === 'flat';
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<AnalyticsStatus | null>(null);
  const [savingProvider, setSavingProvider] = useState<string | null>(null);
  const [syncingSource, setSyncingSource] = useState<string | null>(null);
  const [disconnectingSource, setDisconnectingSource] = useState<string | null>(null);
  const [logpushUpdatingSource, setLogpushUpdatingSource] = useState<string | null>(null);
  const [localSelectedProvider, setLocalSelectedProvider] = useState<IntegrationProvider>('ga4');
  const [propertyIds, setPropertyIds] = useState<Record<Provider, string>>({
    ga4: '',
    clarity: '',
    bing_webmaster: '',
    cloudflare: '',
  });
  const [cloudflareAccountId, setCloudflareAccountId] = useState('');
  const [cloudflareZoneId, setCloudflareZoneId] = useState('');
  const [secrets, setSecrets] = useState<Record<Provider, string>>({
    ga4: '',
    clarity: '',
    bing_webmaster: '',
    cloudflare: '',
  });

  const clearConnectionDraft = useConnectorDraft(
    user?.uid,
    currentProject?.id,
    'analytics',
    {
      ga4: propertyIds.ga4,
      clarity: propertyIds.clarity,
      bing_webmaster: propertyIds.bing_webmaster,
      cloudflareAccountId,
      cloudflareZoneId,
    },
    draft => {
      setPropertyIds(current => ({
        ...current,
        ga4: draft.ga4 || '',
        clarity: draft.clarity || '',
        bing_webmaster: draft.bing_webmaster || '',
      }));
      setCloudflareAccountId(draft.cloudflareAccountId || '');
      setCloudflareZoneId(draft.cloudflareZoneId || '');
    }
  );

  const sourcesByProvider = useMemo(() => {
    const map = new Map<AnalyticsSource['provider'], AnalyticsSource[]>();
    status?.sources.forEach(source => {
      const sources = map.get(source.provider) || [];
      sources.push(source);
      map.set(source.provider, sources);
    });
    return map;
  }, [status?.sources]);

  const fetchStatus = useCallback(
    async (background = false) => {
      if (!user || !currentProject?.id) return;
      if (!background) setLoading(true);
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `/api/integrations/analytics/status?projectId=${encodeURIComponent(currentProject.id)}&ensureGa4ServiceAccount=${!background}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Failed to load analytics integrations');
        const nextStatus: AnalyticsStatus = await response.json();
        setStatus(previous =>
          background && previous
            ? { ...nextStatus, ga4ServiceAccount: previous.ga4ServiceAccount }
            : nextStatus
        );
      } catch (error) {
        console.error(error);
        if (!background) setup.reportError('Analytics integrations could not be loaded.');
      } finally {
        if (!background) setLoading(false);
      }
    },
    [currentProject?.id, user]
  );

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const hasRunningSync = status?.sources.some(source => source.lastSyncStatus === 'started');
  useEffect(() => {
    if (!hasRunningSync) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      await fetchStatus(true);
      if (!cancelled) timer = setTimeout(poll, 5000);
    };
    timer = setTimeout(poll, 5000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hasRunningSync, fetchStatus]);

  const connectProvider = async (provider: Provider) => {
    if (!user || !currentProject?.id) return;
    setup.clearError();
    setSavingProvider(provider);
    try {
      const token = await user.getIdToken();
      const cloudflareAccountToken = isCloudflareAccountTokenValue(secrets.cloudflare);
      const body =
        provider === 'ga4'
          ? { provider, projectId: currentProject.id, propertyId: propertyIds.ga4 }
          : provider === 'clarity'
            ? {
                provider,
                projectId: currentProject.id,
                propertyUrl: propertyIds.clarity,
                token: secrets.clarity,
              }
            : provider === 'cloudflare'
              ? {
                  provider,
                  projectId: currentProject.id,
                  accountId: cloudflareAccountToken ? cloudflareAccountId : undefined,
                  zoneId: cloudflareAccountToken ? undefined : cloudflareZoneId,
                  token: secrets.cloudflare,
                }
              : {
                  provider,
                  projectId: currentProject.id,
                  siteUrl: propertyIds.bing_webmaster,
                  apiKey: secrets.bing_webmaster,
                };
      const response = await fetch('/api/integrations/analytics/connect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok)
        throw new Error(
          data.error ||
            `Connection response failed (HTTP ${response.status}). Refresh status before retrying; setup may have completed.`
        );
      if (data.syncWarning || data.initialSync?.status === 'failed') {
        toast.warning(
          data.syncWarning ||
            data.initialSync?.errorMessage ||
            `${PROVIDER_COPY[provider].title} connected, but initial sync failed.`
        );
      } else {
        toast.success(
          data.initialSync?.status === 'started'
            ? `${PROVIDER_COPY[provider].title} connected. First sync is running; you can close this dialog.`
            : `${PROVIDER_COPY[provider].title} connected.`
        );
      }
      setPropertyIds(prev => ({ ...prev, [provider]: '' }));
      if (provider === 'cloudflare') {
        setCloudflareAccountId('');
        setCloudflareZoneId('');
      }
      setSecrets(prev => ({ ...prev, [provider]: '' }));
      await fetchStatus();
      clearConnectionDraft(
        provider === 'cloudflare' ? ['cloudflareAccountId', 'cloudflareZoneId'] : [provider]
      );
      setup.onConfigured();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Connection failed.');
    } finally {
      setSavingProvider(null);
    }
  };

  const updateMcpExposure = async (source: AnalyticsSource, exposeToMcp: boolean) => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/analytics/sources', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sourceId: source.id, exposeToMcp }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'MCP exposure update failed');
      }
      toast.success(exposeToMcp ? 'Source available to MCP.' : 'Source hidden from MCP.');
      await fetchStatus();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'MCP exposure update failed.');
      await fetchStatus();
    }
  };

  const syncSource = async (source: AnalyticsSource) => {
    if (!user) return;
    setSyncingSource(source.id);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/analytics/sync', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sourceId: source.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status === 'failed') {
        throw new Error(data.errorMessage || data.error || 'Sync failed');
      }
      toast.success(`Synced ${data.rowsUpserted || 0} aggregate rows.`);
      await fetchStatus();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Sync failed.');
    } finally {
      setSyncingSource(null);
    }
  };

  const disconnectSource = async (source: AnalyticsSource) => {
    if (!user || !currentProject?.id) return;
    setDisconnectingSource(source.id);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/analytics/disconnect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ projectId: currentProject.id, sourceId: source.id }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Disconnect failed');
      }
      toast.success('Analytics source disconnected.');
      await fetchStatus();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Disconnect failed.');
    } finally {
      setDisconnectingSource(null);
    }
  };

  const updateCloudflareLogpush = async (source: AnalyticsSource, enabled: boolean) => {
    if (!user || source.provider !== 'cloudflare') return;
    setLogpushUpdatingSource(source.id);
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        enabled
          ? '/api/integrations/analytics/cloudflare/logpush/setup'
          : '/api/integrations/analytics/cloudflare/logpush/delete',
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sourceId: source.id }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.error || 'Logpush update failed');
      }
      toast.success(enabled ? 'Logpush enabled.' : 'Logpush disabled.');
      await fetchStatus();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Logpush update failed.');
    } finally {
      setLogpushUpdatingSource(null);
    }
  };

  const copyServiceAccount = async () => {
    if (!status?.ga4ServiceAccountEmail) return;
    await navigator.clipboard.writeText(status.ga4ServiceAccountEmail);
    toast.success('Service account copied.');
  };

  if (loading) {
    return (
      <IntegrationCard
        icon={<BarChart3 size={18} />}
        title="Analytics data"
        description="Loading connected analytics sources."
        statusLabel="Loading"
        statusTone="idle"
      >
        <IntegrationSetupError message={setup.error} />
        <IntegrationStatusRow icon={<InlineSpinner size="xs" />} title="Loading status" />
      </IntegrationCard>
    );
  }

  const ga4Sources = sourcesByProvider.get('ga4') || [];
  const gscSources = sourcesByProvider.get('gsc') || [];
  const selectedProvider = selectedProviderOverride || localSelectedProvider;
  const selectedSources = sourcesByProvider.get(selectedProvider) || [];
  const selectedCatalog = PROVIDER_CATALOG[selectedProvider];
  const allSources = status?.sources || [];
  const latestSync =
    allSources
      .map(source => source.lastSyncAt)
      .filter((value): value is string => Boolean(value))
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null;

  const selectedProviderContent = (
    <VStack align="stretch" gap={isFlatSurface ? '3' : '4'}>
      <IntegrationSetupError message={setup.error} />
      {showProviderHeader && (
        <Box
          layerStyle={isFlatSurface ? undefined : 'surfaceCard'}
          borderWidth={isFlatSurface ? '0' : '1px'}
          borderStyle="solid"
          borderColor={isFlatSurface ? 'transparent' : 'ui.border.default'}
          borderRadius={isFlatSurface ? '0' : 'glassCard'}
          p={isFlatSurface ? '0' : { base: '4', md: '5' }}
        >
          <VStack align="stretch" gap="3">
            <HStack justify="space-between" align="flex-start" gap="3" flexWrap="wrap">
              <HStack align="flex-start" gap="3" minW={0}>
                <Box
                  boxSize="10"
                  borderRadius="glassButton"
                  bg="ui.surface.selected"
                  color="ui.text.primary"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  {selectedCatalog.icon}
                </Box>
                <Box minW={0}>
                  <HStack gap="2" flexWrap="wrap">
                    <Text fontSize="lg" fontWeight="700" color="ui.text.primary">
                      {selectedCatalog.title}
                    </Text>
                    <Badge
                      colorPalette={badgePalette(sourcesTone(selectedSources))}
                      variant="subtle"
                    >
                      {sourcesStatusLabel(selectedSources)}
                    </Badge>
                  </HStack>
                  <Text color="ui.text.secondary" fontSize="sm" mt="1">
                    {selectedCatalog.description}
                  </Text>
                </Box>
              </HStack>
              <Badge bg="accent.subtle" color="accent.fg" variant="subtle">
                {selectedCatalog.auth}
              </Badge>
            </HStack>

            <HStack gap="2" flexWrap="wrap">
              <ToolChip icon={<Wrench size={13} />} label="tools" />
              {selectedCatalog.tools.map(tool => (
                <ToolChip key={tool} icon={<ShieldCheck size={13} />} label={tool} />
              ))}
            </HStack>
          </VStack>
        </Box>
      )}

      {selectedProvider === 'ga4' && (
        <Box
          layerStyle={isFlatSurface ? undefined : 'surfaceCard'}
          borderWidth={isFlatSurface ? '0' : '1px'}
          borderStyle="solid"
          borderColor={isFlatSurface ? 'transparent' : 'ui.border.default'}
          borderRadius={isFlatSurface ? '0' : 'glassCard'}
          p={isFlatSurface ? '0' : { base: '4', md: '5' }}
        >
          <VStack align="stretch" gap="3">
            <IntegrationStatusRow
              icon={<KeyRound size={16} />}
              title="Attune-managed service account"
              description={ga4IdentityDescription(status)}
              action={
                <Button size="sm" variant="outline" onClick={copyServiceAccount}>
                  <Clipboard size={14} />
                  <Text ml="1">Copy</Text>
                </Button>
              }
            >
              <Text fontSize="xs" color="ui.text.secondary" wordBreak="break-all" mt="1">
                {status?.ga4ServiceAccountEmail}
              </Text>
            </IntegrationStatusRow>
            {status?.ga4ServiceAccount?.status === 'error' && (
              <IntegrationNotice tone="danger" icon={<AlertCircle size={16} />}>
                Attune could not prepare the GA4 reader automatically.
              </IntegrationNotice>
            )}
            <ProviderForm
              provider="ga4"
              sources={ga4Sources}
              propertyValue={propertyIds.ga4}
              onPropertyChange={value => setPropertyIds(prev => ({ ...prev, ga4: value }))}
              saving={savingProvider === 'ga4'}
              syncingSource={syncingSource}
              disconnectingSource={disconnectingSource}
              logpushUpdatingSource={logpushUpdatingSource}
              onConnect={() => connectProvider('ga4')}
              onSync={syncSource}
              onDisconnect={disconnectSource}
              onMcpChange={updateMcpExposure}
              onLogpushChange={updateCloudflareLogpush}
            />
          </VStack>
        </Box>
      )}

      {selectedProvider === 'gsc' && (
        <Box aria-label={GSC_PROVIDER_LABEL}>
          <GSCIntegration />
          {gscSources.length > 0 && (
            <Box
              layerStyle={isFlatSurface ? undefined : 'surfaceCard'}
              borderWidth={isFlatSurface ? '0' : '1px'}
              borderStyle="solid"
              borderColor={isFlatSurface ? 'ui.border.subtle' : 'ui.border.default'}
              borderRadius={isFlatSurface ? '0' : 'glassCard'}
              p={isFlatSurface ? '0' : { base: '4', md: '5' }}
              mt={isFlatSurface ? '3' : '4'}
              pt={isFlatSurface ? '3' : undefined}
              borderTopWidth={isFlatSurface ? '1px' : undefined}
            >
              <VStack align="stretch" gap="0">
                {gscSources.map(source => (
                  <ConnectedSourceRow
                    key={source.id}
                    source={source}
                    syncing={syncingSource === source.id}
                    disconnecting={disconnectingSource === source.id}
                    logpushUpdating={logpushUpdatingSource === source.id}
                    onSync={syncSource}
                    onDisconnect={disconnectSource}
                    onMcpChange={updateMcpExposure}
                    onLogpushChange={updateCloudflareLogpush}
                  />
                ))}
              </VStack>
            </Box>
          )}
        </Box>
      )}

      {(selectedProvider === 'clarity' ||
        selectedProvider === 'bing_webmaster' ||
        selectedProvider === 'cloudflare') && (
        <Box
          layerStyle={isFlatSurface ? undefined : 'surfaceCard'}
          borderWidth={isFlatSurface ? '0' : '1px'}
          borderStyle="solid"
          borderColor={isFlatSurface ? 'transparent' : 'ui.border.default'}
          borderRadius={isFlatSurface ? '0' : 'glassCard'}
          p={isFlatSurface ? '0' : { base: '4', md: '5' }}
        >
          <ProviderForm
            provider={selectedProvider}
            sources={selectedSources}
            propertyValue={
              selectedProvider === 'cloudflare'
                ? isCloudflareAccountTokenValue(secrets.cloudflare)
                  ? cloudflareAccountId
                  : cloudflareZoneId
                : propertyIds[selectedProvider]
            }
            secretValue={secrets[selectedProvider]}
            onPropertyChange={value => {
              if (selectedProvider === 'cloudflare') {
                if (isCloudflareAccountTokenValue(secrets.cloudflare)) {
                  setCloudflareAccountId(value);
                } else {
                  setCloudflareZoneId(value);
                }
                return;
              }
              setPropertyIds(prev => ({ ...prev, [selectedProvider]: value }));
            }}
            onSecretChange={value => setSecrets(prev => ({ ...prev, [selectedProvider]: value }))}
            saving={savingProvider === selectedProvider}
            syncingSource={syncingSource}
            disconnectingSource={disconnectingSource}
            logpushUpdatingSource={logpushUpdatingSource}
            onConnect={() => connectProvider(selectedProvider)}
            onSync={syncSource}
            onDisconnect={disconnectSource}
            onMcpChange={updateMcpExposure}
            onLogpushChange={updateCloudflareLogpush}
          />
        </Box>
      )}
    </VStack>
  );

  if (!showCatalog) return selectedProviderContent;

  return (
    <Grid templateColumns={{ base: '1fr', xl: '320px minmax(0, 1fr)' }} gap="4" alignItems="start">
      <GridItem
        layerStyle="dataTableSurface"
        borderWidth={{ base: '1px', md: '0' }}
        borderStyle="solid"
        borderColor="ui.border.default"
        borderRadius="0"
        boxShadow="none"
        p="4"
      >
        <VStack align="stretch" gap="4">
          <HStack align="flex-start" gap="3">
            <Box
              boxSize="9"
              borderRadius="glassButton"
              bg="ui.surface.selected"
              color="ui.text.primary"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Plug size={18} />
            </Box>
            <Box minW={0}>
              <HStack gap="2" flexWrap="wrap">
                <Text fontWeight="650" color="ui.text.primary">
                  Data source tools
                </Text>
                <Badge colorPalette={allSources.length > 0 ? 'green' : 'gray'} variant="subtle">
                  {allSources.length} active
                </Badge>
              </HStack>
              <Text fontSize="sm" color="ui.text.secondary" mt="1">
                Provider names stay here; Pages and Website use the normalized data.
              </Text>
            </Box>
          </HStack>

          <VStack align="stretch" gap="2">
            {PROVIDER_ORDER.map(provider => (
              <ProviderToolButton
                key={provider}
                provider={provider}
                selected={selectedProvider === provider}
                sources={sourcesByProvider.get(provider) || []}
                onSelect={() => setLocalSelectedProvider(provider)}
              />
            ))}
          </VStack>

          <Box borderTopWidth="1px" borderTopStyle="solid" borderTopColor="ui.border.subtle" pt="3">
            <VStack align="stretch" gap="2">
              <ToolRailMetric
                icon={<Database size={14} />}
                label="MCP exposed"
                value={`${allSources.filter(source => source.exposeToMcp).length}/${allSources.length}`}
              />
              <ToolRailMetric
                icon={<RefreshCw size={14} />}
                label="Last sync"
                value={formatDate(latestSync)}
              />
            </VStack>
          </Box>
        </VStack>
      </GridItem>

      <GridItem minW={0}>{selectedProviderContent}</GridItem>
    </Grid>
  );
}

function ProviderForm({
  provider,
  sources,
  propertyValue,
  secretValue,
  saving,
  syncingSource,
  disconnectingSource,
  logpushUpdatingSource,
  onPropertyChange,
  onSecretChange,
  onConnect,
  onSync,
  onDisconnect,
  onMcpChange,
  onLogpushChange,
}: {
  provider: Provider;
  sources: AnalyticsSource[];
  propertyValue: string;
  secretValue?: string;
  saving: boolean;
  syncingSource: string | null;
  disconnectingSource: string | null;
  logpushUpdatingSource: string | null;
  onPropertyChange: (value: string) => void;
  onSecretChange?: (value: string) => void;
  onConnect: () => void;
  onSync: (source: AnalyticsSource) => void;
  onDisconnect: (source: AnalyticsSource) => void;
  onMcpChange: (source: AnalyticsSource, exposeToMcp: boolean) => void;
  onLogpushChange: (source: AnalyticsSource, enabled: boolean) => void;
}) {
  const copy = PROVIDER_COPY[provider];
  const primaryPlaceholder =
    provider === 'cloudflare' && secretValue && !isCloudflareAccountTokenValue(secretValue)
      ? 'Cloudflare zone ID, optional'
      : copy.primaryPlaceholder;

  return (
    <VStack align="stretch" gap="3">
      {sources.map(source => (
        <ConnectedSourceRow
          key={source.id}
          source={source}
          syncing={syncingSource === source.id}
          disconnecting={disconnectingSource === source.id}
          logpushUpdating={logpushUpdatingSource === source.id}
          onSync={onSync}
          onDisconnect={onDisconnect}
          onMcpChange={onMcpChange}
          onLogpushChange={onLogpushChange}
        />
      ))}
      <Field.Root>
        <Field.Label fontSize="xs">{primaryPlaceholder}</Field.Label>
        <Input
          size="sm"
          value={propertyValue}
          onChange={event => onPropertyChange(event.target.value)}
          placeholder={primaryPlaceholder}
        />
      </Field.Root>
      {copy.secretPlaceholder && (
        <Field.Root>
          <Field.Label fontSize="xs">{copy.secretPlaceholder}</Field.Label>
          <Input
            size="sm"
            type="password"
            value={secretValue || ''}
            onChange={event => onSecretChange?.(event.target.value)}
            placeholder={copy.secretPlaceholder}
          />
        </Field.Root>
      )}
      <Button
        size="sm"
        bg="accent.solid"
        color="accent.contrast"
        _hover={{ bg: 'accent.emphasized' }}
        onClick={onConnect}
        disabled={saving}
      >
        {saving ? <InlineSpinner size="xs" /> : <Check size={14} />}
        <Text ml="1">{sources.length > 0 ? 'Add source' : 'Verify and connect'}</Text>
      </Button>
    </VStack>
  );
}

function ConnectedSourceRow({
  source,
  syncing,
  disconnecting,
  logpushUpdating,
  onSync,
  onDisconnect,
  onMcpChange,
  onLogpushChange,
}: {
  source: AnalyticsSource;
  syncing: boolean;
  disconnecting: boolean;
  logpushUpdating: boolean;
  onSync: (source: AnalyticsSource) => void;
  onDisconnect: (source: AnalyticsSource) => void;
  onMcpChange: (source: AnalyticsSource, exposeToMcp: boolean) => void;
  onLogpushChange: (source: AnalyticsSource, enabled: boolean) => void;
}) {
  const logpushJobId = source.sourceConfig?.logpushJobId;
  const canManageLogpush =
    source.provider === 'cloudflare' && source.sourceConfig?.scope !== 'account';
  const logpushEnabled =
    canManageLogpush &&
    (source.sourceConfig?.logpushEnabled === true ||
      (typeof logpushJobId === 'number' && Number.isFinite(logpushJobId)));

  return (
    <>
      <IntegrationStatusRow
        icon={<Check size={16} />}
        title={source.displayName}
        description={
          source.lastSyncStatus === 'started'
            ? 'Connected. Syncing analytics data; you can close this dialog.'
            : `Last sync: ${formatDate(source.lastSyncAt)}`
        }
        action={
          <HStack gap="2" flexWrap="wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSync(source)}
              disabled={syncing || source.lastSyncStatus === 'started'}
            >
              {syncing || source.lastSyncStatus === 'started' ? (
                <InlineSpinner size="xs" />
              ) : (
                <RefreshCw size={14} />
              )}
            </Button>
            {canManageLogpush && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onLogpushChange(source, !logpushEnabled)}
                disabled={logpushUpdating || disconnecting}
              >
                {logpushUpdating ? <InlineSpinner size="xs" /> : <Database size={14} />}
                <Text ml="1">{logpushEnabled ? 'Disable Logpush' : 'Enable Logpush'}</Text>
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              color="ui.text.tertiary"
              onClick={() => onDisconnect(source)}
              disabled={disconnecting}
            >
              {disconnecting ? <InlineSpinner size="xs" /> : <Unlink size={14} />}
            </Button>
          </HStack>
        }
      >
        <HStack mt="2" gap="2" flexWrap="wrap">
          <Badge colorPalette={source.exposeToMcp ? 'green' : 'gray'} variant="subtle">
            Available to MCP
          </Badge>
          {canManageLogpush && (
            <Badge colorPalette={logpushEnabled ? 'green' : 'gray'} variant="subtle">
              {logpushEnabled ? 'Logpush on' : 'Logpush off'}
            </Badge>
          )}
          <Switch.Root
            size="sm"
            checked={source.exposeToMcp}
            onCheckedChange={event => onMcpChange(source, event.checked)}
          >
            <Switch.HiddenInput />
            <Switch.Control />
          </Switch.Root>
        </HStack>
      </IntegrationStatusRow>
      {source.lastSyncStatus === 'failed' && (
        <IntegrationNotice icon={<AlertCircle size={16} />} tone="danger">
          {source.lastSyncError || 'Last sync failed.'}
        </IntegrationNotice>
      )}
    </>
  );
}
